import React, { useEffect, useState } from "react";
import { Modal, Form, Row, Select, Col, Checkbox, Button, message } from "antd";
import moment from "moment";
import { debounce } from "lodash";

import QuotationForm from "../LeadGenerationCP/NewRateCard/QuotationForm";
import { customerOnboardingAPI } from "../../api/LeadApi";
import { masterRateCardAPI } from "../../api/RateCardApi";
import urls from "../../api/urls";
import { capitaliseWithHyphen } from "../../utils";

const { GET_CLIENT_MASTER_LIST_V1, GET_COMPANY_MASTER_LIST_V1 } = urls;

const rateCardTypeOptions = [
  { label: "Cash", value: "CASH" },
  { label: "Account", value: "ACCOUNT" },
];

const assignTypeOptions = [
  { label: "Company", value: "COMPANY" },
  { label: "Client", value: "CLIENT" },
];

const AssignRateCard = ({ isModalOpen, setIsModalOpen, assignRateDetails }) => {
  const [form] = Form.useForm();
  const [companyList, setCompanyList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [assignType, setAssignType] = useState(null);

  useEffect(() => {
    fetchCompanyList();
    fetchClientList();
  }, []);

  const fetchMasterList = async (apiFn, params, url, formatter) => {
    const response = await apiFn(params, url);
    const data = response?.response?.data?.data || [];
    return data.map(formatter);
  };

  const fetchCompanyList = async (params = {}) => {
    const companies = await fetchMasterList(
      customerOnboardingAPI.getMasteList,
      params,
      GET_COMPANY_MASTER_LIST_V1,
      (item) => ({
        label: capitaliseWithHyphen(item?.orgName),
        value: String(item?.customerData?.companyId),
        key: String(item?.customerData?.companyId),
      })
    );
    setCompanyList(companies);
  };

  const fetchClientList = async (params = {}) => {
    const clients = await fetchMasterList(
      customerOnboardingAPI.getMasteList,
      params,
      GET_CLIENT_MASTER_LIST_V1,
      (item) => ({
        label: capitaliseWithHyphen(item?.orgName),
        value: String(item?.customerData?.clientLocationId),
        key: String(item?.customerData?.clientLocationId),
      })
    );
    setClientList(clients);
  };

  const debouncedSearch = {
    company: debounce((value) => fetchCompanyList({ orgName: value }), 500),
    client: debounce((value) => fetchClientList({ orgName: value }), 500),
  };

  const handleSubmit = async (values) => {
    if (!values?.companyId && !values?.clientId) {
      message.error("Please select at least one company or client");
      return;
    }

    const userId = [...(values?.companyId || []), ...(values?.clientId || [])];

    const payload = {
      refId: assignRateDetails?.rateCardId,
      users: [
        {
          userId,
          type: values?.type,
          commitedBusiness: values?.commitedBusiness,
          invoiceType: values?.invoiceType,
          billingCycle: values?.billingCycle,
          fromDate: moment(values?.fromDate).format("YYYY-MM-DD"),
          toDate: moment(values?.toDate).format("YYYY-MM-DD"),
        },
      ],
    };

    const response = await masterRateCardAPI.assignRateCard(payload);
    if (response?.status) {
      message.success("Rate card assigned successfully");
      handleCancel();
    } else {
      message.error(
        response?.response?.data?.message || "Failed to assign rate card"
      );
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setAssignType(null);
    form.resetFields();
  };

  return (
    <Modal
      title={`Assign Rate Card (${assignRateDetails?.name})`}
      open={isModalOpen}
      onCancel={handleCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={() => form.submit()}>
          Submit
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        name="assignRateCardForm"
        onFinish={handleSubmit}
      >
        <Row gutter={20} className="global-form-row p-1">
          <QuotationForm hideSelectTemplate colSpan={8} />

          <Col span={8}>
            <Form.Item
              name="type"
              label="Ratecard Type"
              rules={[
                { required: true, message: "Please select ratecard type" },
              ]}
            >
              <Checkbox.Group options={rateCardTypeOptions} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="assignType"
              label=""
              rules={[
                { required: true, message: "Please choose one of the options" },
              ]}
            >
              <Checkbox.Group
                options={assignTypeOptions}
                onChange={(value) => setAssignType(value)}
              />
            </Form.Item>
          </Col>

          {assignType?.includes("COMPANY") && (
            <Col span={24}>
              <Form.Item name="companyId" label="Select Company">
                <Select
                  mode="multiple"
                  options={companyList}
                  placeholder="Search & select company"
                  showSearch
                  onSearch={debouncedSearch.company}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          )}

          {assignType?.includes("CLIENT") && (
            <Col span={24}>
              <Form.Item name="clientId" label="Select Client">
                <Select
                  mode="multiple"
                  options={clientList}
                  placeholder="Search & select client"
                  showSearch
                  onSearch={debouncedSearch.client}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default AssignRateCard;
